import { initLogger } from '@monofront/logging';
import logger from '@utils/logger';
initLogger(logger);

import InformationsModal from '@components/InformationsModal';
import Vendors from '@components/Vendors';
import { AlcoholEligibilityProvider } from '@context/AlcoholEligibilityContext';

import '../globals.css';
import '@monofront/design-system/components/Carousel/carousel.css';
import {
  AuthProvider,
  WishlistProvider,
  useAuthentication,
} from '@context/businessContainer';
import {
  CheckoutCartIdSyncWithMobileApp,
  CheckoutCartMachineProvider,
} from '@context/checkoutCartMachineContext';
import { DeliverBarContextProvider } from '@context/DeliveryBarContext';
import { EditorialSidePanelContextProvider } from '@context/EditorialSidePanelContextProvider';
import { GiftItemProvider } from '@context/giftItem';
import { GlobalSearchContextProvider } from '@context/GlobalSearchContext';
import { GoogleShoppingProvider } from '@context/googleShopping';
import {
  GrowthBookProvider,
  SyncGrowthBookAttributesProvider,
} from '@context/growthbook';
import { LayoutContextProvider } from '@context/LayoutContext';
import { LayoutRefsProvider } from '@context/layoutRefs';
import { MembershipCouponProvider } from '@context/membershipCoupon';
import { PersistedAuthProvider } from '@context/persistedAuthContext';
import { ReferralContextProvider } from '@context/referral';
import { SearchPanelProvider } from '@context/SearchPanelContext';
import { ShoppingCartProvider } from '@context/shoppingCart';
import { TrackingProvider } from '@context/tracking';
import ModalAgeVerification from '@features/AlcoholOptIn/ModalAgeVerification';
import { DeliveryBarSidePanel } from '@features/DeliveryBar';
import EditorialSidePanel from '@features/EditorialSidePanel';
import withApollo from '@hocs/withApollo';
import {
  CheckoutCartProvider,
  EligibilityProvider,
  GiftItemBannerProvider,
  LinkingContextProvider,
  ProductCustomerProvider,
  ProductLayoutProvider,
} from '@monofront/business/contexts';
import { useLocalStorage } from '@monofront/business/hooks';
import DefaultLayout from '@components/Layout';
import FillEmailModal from '@components/FillEmailModal';
import LocalizedDefaultSeo from '@components/LocalizedDefaultSeo';
import { ApolloProvider } from '@apollo/client';
import { initFaro } from '@utils/faro';
import { FaroErrorBoundary } from '@grafana/faro-react';
import {
  getChannelFromLocale,
  getLocationFromSessionStorage,
  updateLocationSessionStorage,
} from '@monofront/business/utils';
import { trackEvent } from '@monofront/tracking';
import { PrismicLinkContext } from '@monofront/types';
import { PrismicPreview } from '@prismicio/next';
import { customerCRUD, prismicService } from '@services/businessContainer';
import '@styles/global.css';
import '@styles/page-loader.css';
import { toastifyCss } from '@styles/toastify';
import { variables } from '@styles/variables';
import { isMobileApp } from '@utils/environment';
import { filterLogs } from '@utils/logsFilter';
import { isInPrismicPreview } from '@utils/prismic';
import { NextIntlClientProvider } from 'next-intl';
import getConfig from 'next/config';
import dynamic from 'next/dynamic';
import Router from 'next/router';
import NProgress from 'nprogress';
import { useEffect, useState } from 'react';
import { Cookies, CookiesProvider } from 'react-cookie';
import { ToastContainer } from 'react-toastify';
import '../globals.css';
import { DeliveryBarError } from '../src/features/DeliveryBar/ErrorMessages/DeliveryBarError';

const ServiceWidgets = dynamic(
  async () => (await import('@components/ServiceWidgets')).ServiceWidgets,
  {
    ssr: false,
  },
);

filterLogs();

const {
  publicRuntimeConfig: {
    faro,
    prismicConfig: { repository },
  },
} = getConfig();

initFaro(faro);

Router.events.on('routeChangeStart', () => NProgress.start());
Router.events.on('routeChangeComplete', () => NProgress.done());
Router.events.on('routeChangeError', () => NProgress.done());

const ShopApp = ({ Component, pageProps, apolloClient, cookieString }) => {
  const {
    locale,
    productLayout,
    giftsItems,
    globalLayout,
    megamenu,
    megamenuEntries,
    featureFlags,
    globalGiftItemBanner,
    searchPanel,
    nutritionalValues,
    deliveryBarContent,
  } = pageProps;

  const giftItemBannerProps = globalGiftItemBanner?.results;

  const Layout = Component.getLayout
    ? Component.getLayout(pageProps)
    : DefaultLayout;

  const [layoutState, setLayoutState] = useState();
  const [giftItemsState, setGiftItemsState] = useState();
  const [productLayoutState, setProductLayoutState] = useState();
  const [productNutritionalValuesState, setProductNutritionalValuesState] =
    useState();
  const [deliveryBarContentState, setDeliveryBarContentState] = useState({});

  // The Prismic toolbar (loaded by PrismicPreview) must only run for editors in
  // an active preview session. Mounting it for every visitor makes each page load
  // fetch the toolbar iframe from Prismic, which got our repository rate-limited.
  // Resolved in an effect (not at render) to avoid an SSR/client hydration mismatch.
  const [isPreview, setIsPreview] = useState(false);

  useEffect(() => {
    setIsPreview(isInPrismicPreview());
  }, []);

  useEffect(() => {
    window.onerror = (event, source, lineno, colno, error) => {
      logger.error('External script thrown an error', {
        externalError: {
          source,
          lineno,
          colno,
          error,
          event,
        },
      });
    };

    return () => {
      window.onerror = null;
    };
  }, []);

  useEffect(() => {
    setLayoutState(globalLayout);
    setGiftItemsState(giftsItems);
    setProductLayoutState(productLayout);
    setProductNutritionalValuesState(nutritionalValues);
    setDeliveryBarContentState(deliveryBarContent);
  }, []);

  useEffect(() => {
    if (isMobileApp) {
      return;
    }

    updateLocationSessionStorage(getLocationFromSessionStorage() as any); // to type !

    const promptListener = (event) => {
      event.preventDefault();
      window.removeEventListener('beforeinstallprompt', promptListener);
    };

    // deprecated tracker https://gitlab.com/la-fourche/scrum/-/issues/1968
    // pwa deprecated in favor of native apps
    // const installListener = () => dataLayerTrackEvent('pwaInstalled');
    const installListener = () => trackEvent({ type: 'LFPWAInstalled' });

    window.addEventListener('beforeinstallprompt', promptListener);
    window.addEventListener('appinstalled', installListener);

    return () => {
      window.removeEventListener('beforeinstallprompt', promptListener);
      window.removeEventListener('appinstalled', installListener);
    };
  }, []);

  const appContent = (
    <NextIntlClientProvider
      timeZone="Europe/Paris"
      locale={locale}
      messages={pageProps.messages}
    >
      <CookiesProvider cookies={new Cookies(cookieString)}>
        <GrowthBookProvider featureFlags={featureFlags}>
          <Vendors />
          <ApolloProvider client={apolloClient}>
            <LocalizedDefaultSeo />
            <ToastContainer
              progressClassName="toast-success"
              className="toast-black-text"
            />
            <AuthProvider>
              <PersistedAuthProvider>
                <ProductCustomerProvider
                  // Don't provide useCacheFirstAuthentication, as it causes
                  // hydration mismatches when the user is already authenticated
                  useAuthentication={useAuthentication}
                >
                  <ServiceWidgets />
                  <ShoppingCartProvider channel={getChannelFromLocale(locale)}>
                    <AlcoholEligibilityProvider>
                      <WishlistProvider channel={getChannelFromLocale(locale)}>
                        <EligibilityProvider
                          useAuthentication={useAuthentication}
                          useLocalStorage={useLocalStorage}
                          customerCRUD={customerCRUD}
                        >
                          <SearchPanelProvider prismicSearchPanel={searchPanel}>
                            <GoogleShoppingProvider>
                              <GiftItemProvider
                                products={giftItemsState || giftsItems}
                              >
                                <ReferralContextProvider>
                                  <MembershipCouponProvider>
                                    <SyncGrowthBookAttributesProvider />
                                    <TrackingProvider />
                                    <ProductLayoutProvider
                                      productLayout={
                                        productLayoutState || productLayout
                                      }
                                      nutritionalValues={
                                        productNutritionalValuesState ||
                                        nutritionalValues
                                      }
                                    >
                                      <CheckoutCartProvider
                                        useAuthentication={useAuthentication}
                                        isMobileApp={isMobileApp}
                                      >
                                        <CheckoutCartIdSyncWithMobileApp />
                                        <CheckoutCartMachineProvider>
                                          <LayoutContextProvider
                                            value={{
                                              layout:
                                                layoutState || globalLayout,
                                              megamenu,
                                              megamenuEntries,
                                              props: pageProps,
                                            }}
                                          >
                                            <DeliverBarContextProvider
                                              content={deliveryBarContentState}
                                            >
                                              <DeliveryBarError />
                                              <EditorialSidePanelContextProvider
                                                prismicService={prismicService}
                                                previewData={
                                                  Component.previewData
                                                }
                                                locale={locale}
                                              >
                                                <InformationsModal />
                                                <ModalAgeVerification />
                                                <EditorialSidePanel />
                                                <DeliveryBarSidePanel />
                                                {isMobileApp &&
                                                !Component.hasAppMobileLayout ? (
                                                  <Component {...pageProps} />
                                                ) : (
                                                  <GiftItemBannerProvider
                                                    contents={
                                                      giftItemBannerProps
                                                    }
                                                  >
                                                    <Layout
                                                      pageProps={pageProps}
                                                    >
                                                      <Component
                                                        variant={false}
                                                        {...pageProps}
                                                      />
                                                    </Layout>
                                                  </GiftItemBannerProvider>
                                                )}
                                              </EditorialSidePanelContextProvider>
                                              <FillEmailModal />
                                            </DeliverBarContextProvider>
                                          </LayoutContextProvider>
                                        </CheckoutCartMachineProvider>
                                      </CheckoutCartProvider>
                                    </ProductLayoutProvider>
                                  </MembershipCouponProvider>
                                </ReferralContextProvider>
                              </GiftItemProvider>
                            </GoogleShoppingProvider>
                          </SearchPanelProvider>
                        </EligibilityProvider>
                      </WishlistProvider>
                    </AlcoholEligibilityProvider>
                  </ShoppingCartProvider>
                </ProductCustomerProvider>
              </PersistedAuthProvider>
            </AuthProvider>
            <style jsx global>
              {variables}
            </style>
            <style jsx global>
              {toastifyCss}
            </style>
          </ApolloProvider>
        </GrowthBookProvider>
      </CookiesProvider>
    </NextIntlClientProvider>
  );

  return (
    <FaroErrorBoundary>
      <LinkingContextProvider linkingContext={PrismicLinkContext.SHOP}>
      <LayoutRefsProvider>
        <GlobalSearchContextProvider>
          {isPreview ? (
            <PrismicPreview repositoryName={repository}>
              {appContent}
            </PrismicPreview>
          ) : (
            appContent
          )}
        </GlobalSearchContextProvider>
      </LayoutRefsProvider>
    </LinkingContextProvider>
    </FaroErrorBoundary>
  );
};

export default withApollo(ShopApp);
